import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Actions, Card, Table, useNotify, Loader, Space, Icon, Button } from 'ebs-design';
import { Pagination } from 'components/organisms';
import { ConfirmModal } from 'components/molecules';
import { useQueryParams, useQueryUpdate } from 'hooks/useQueryParams';
import { Plus } from 'resources/icons';
import { FormModal } from '../FormModal';
import { FormNomenclatures } from '../FormNomenclatures';
import { Results, Column, FilterType, ActivityDomains } from 'types';
import { useActivityDomainsAPI } from 'api';
import { defaultFilters } from 'utils';
import { useRolePermission } from 'hooks/useRolePermission';
import { UserRoleCRM } from 'hooks/useFetcher/interfaces';

const formId = 'activity-domains';

export const NomenclaturesActivityDomains: React.FC = () => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { updateQuery } = useQueryUpdate();
  const {
    getActivityDomains,
    getActivityDomainById,
    createActivityDomain,
    updateActivityDomain,
    deleteActivityDomain,
  } = useActivityDomainsAPI();

  const [isCreate, setIsCreate] = React.useState<boolean>(false);
  const [editItem, setEditItem] = React.useState();
  const [deleteItem, setDeleteItem] = React.useState();

  const hasEditPermission = useRolePermission([UserRoleCRM.admin]);

  const { data: activeDomains, isLoading } = useQuery<Results<ActivityDomains>>(
    ['activity-domains', { ...defaultFilters, per_page: 100, ...params }],
    getActivityDomains,
    {
      onError: () => {
        notify.error({ title: t('error.someThingIsWrong') });
      },
    },
  );

  const deleteActivityDomainMutation = useMutation(deleteActivityDomain, {
    onError: () => notify.error({ title: t('error.someThingIsWrong') }),
    onSuccess: () => {
      queryClient.invalidateQueries(['activity-domains']);
      setDeleteItem(undefined);
      notify.success({ title: t('success.successDataDelete') });
    },
  });

  const handleDelete = (): void => {
    if (deleteItem) {
      deleteActivityDomainMutation.mutate(deleteItem);
    }
  };

  const handleCancelDelete = (): void => {
    setDeleteItem(undefined);
  };

  const handleCloseModal = (): void => {
    setEditItem(undefined);
    setIsCreate(false);
  };

  const handleFilter = (type: FilterType, field: string): FilterType => {
    updateQuery({ ...params, ordering: type ? `${type === 'desc' ? '-' : ''}${field}` : type });
    return type;
  };

  const columns: Column<ActivityDomains>[] = React.useMemo(
    () => [
      {
        title: t('nomenclature.activityDomains'),
        onFilter: (type) => handleFilter(type, 'name'),
        render: ({ name }): React.ReactNode => name,
      },
      ...(hasEditPermission
        ? [
            {
              align: 'right',
              render: ({ id }) => (
                <Space justify="end">
                  <Actions placement="bottom">
                    <Actions.Item onClick={() => setEditItem(id)}>
                      {t('nomenclature.edit')}
                    </Actions.Item>
                    <Actions.Item onClick={() => setDeleteItem(id)}>
                      {t('nomenclature.delete')}
                    </Actions.Item>
                  </Actions>
                </Space>
              ),
            } as Column<ActivityDomains>,
          ]
        : []),
    ],
    [t, activeDomains, params],
  );

  return (
    <>
      <Space justify="space-between" className="mb-20">
        <h3 className="title">{`${t(`nomenclature.activityDomains`)}${
          activeDomains?.count ? ` (${activeDomains.count})` : ``
        }`}</h3>

        {hasEditPermission && (
          <Button
            type="primary"
            prefix={<Icon component={Plus} />}
            onClick={() => setIsCreate(true)}
          >
            {t('buttons.addNew')}
          </Button>
        )}
      </Space>

      <Card>
        <Loader loading={isLoading} size="regular">
          <Table
            emptyText={t('main.noData')}
            data={activeDomains?.results}
            columns={columns}
            className="no-border"
          />
        </Loader>
        <Card.Footer>
          <Pagination size={activeDomains?.total_pages || 0} />
        </Card.Footer>
      </Card>

      {!!deleteItem && <ConfirmModal onSuccess={handleDelete} onCancel={handleCancelDelete} />}

      {(editItem || isCreate) && (
        <FormModal formId={formId} onCancel={handleCloseModal} isCreate={isCreate}>
          <FormNomenclatures
            formId={formId}
            getItem={getActivityDomainById}
            onFinish={isCreate ? createActivityDomain : updateActivityDomain}
            onClose={handleCloseModal}
            id={editItem}
          />
        </FormModal>
      )}
    </>
  );
};
